import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';
import type { Aluno } from '../../types/models';

export type AlunoProfessorPick = Pick<
  Aluno,
  'id' | 'nome' | 'matricula' | 'fotoUrl' | 'turmaId' | 'genero'
> & {
  turma?: { id: string; nome: string };
};

/**
 * Alunos das turmas do professor via chamada (escopo validado no backend).
 * Evita GET /alunos com lista completa da escola.
 */
export function useMeusAlunosProfessor(turmaIds: string[]) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const ids = useMemo(() => [...new Set(turmaIds.filter(Boolean))], [turmaIds]);

  const queries = useQueries({
    queries: ids.map((turmaId) => ({
      queryKey: ['chamada-alunos-turma', escolaId, turmaId],
      queryFn: async () => {
        const { data } = await api.get<{
          turma: { id: string; nome: string };
          alunos: AlunoProfessorPick[];
        }>(`/escolas/${escolaId}/chamadas/turma/${turmaId}/alunos`);
        const turma = data?.turma;
        return (data?.alunos ?? []).map((a) => ({
          ...a,
          turmaId: a.turmaId ?? turmaId,
          turma: a.turma ?? (turma ? { id: turma.id, nome: turma.nome } : undefined),
        }));
      },
      enabled: !!escolaId && !!turmaId,
      staleTime: 60_000,
    })),
  });

  const alunos = useMemo(() => {
    const byId = new Map<string, AlunoProfessorPick>();
    for (const q of queries) {
      for (const a of q.data ?? []) {
        if (!byId.has(a.id)) byId.set(a.id, a);
      }
    }
    return Array.from(byId.values()).sort((a, b) =>
      (a.nome || '').localeCompare(b.nome || '', 'pt-BR'),
    );
  }, [queries]);

  return {
    alunos,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
}
